import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  LinearProgress,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { ReloadOutlined, CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { appInsights } from 'utils/appInsights';
import { useUserInfo } from 'hooks/useUserInfo';

interface MandatoryReloadDialogProps {
  open: boolean;
  version: string;
  message: string;
  countdownSeconds: number;
  sentBy?: string;
  onReload: () => void;
  onCancel: () => void;
}

/**
 * Dialog showing mandatory reload with countdown timer
 * 
 * Features:
 * - Shows countdown timer
 * - Progress bar visualization
 * - User can cancel (stops countdown)
 * - User can reload immediately
 * - Auto-reloads when countdown reaches 0
 * 
 * @example
 * ```tsx
 * <MandatoryReloadDialog
 *   open={isMandatory && hasUpdate}
 *   version="1.0.142"
 *   message="Critical security update"
 *   countdownSeconds={30}
 *   sentBy="admin"
 *   onReload={reloadApp}
 *   onCancel={handleCancel}
 * />
 * ```
 */
export default function MandatoryReloadDialog({
  open,
  version,
  message,
  countdownSeconds: initialCountdown,
  sentBy,
  onReload,
  onCancel
}: MandatoryReloadDialogProps) {
  const { userName } = useUserInfo();
  const [countdown, setCountdown] = useState(initialCountdown);
  const [isPaused, setIsPaused] = useState(false);

  // Reset countdown when dialog opens
  useEffect(() => {
    if (open) {
      setCountdown(initialCountdown);
      setIsPaused(false);
    }
  }, [open, initialCountdown]);

  // Countdown timer
  useEffect(() => {
    if (!open || isPaused || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Countdown finished, reload automatically
          console.log('[MandatoryReload] Countdown finished, reloading...');
          
          // Track auto-reload in Application Insights
          appInsights.trackEvent({
            name: 'VersionControl_AutoReloadTriggered',
            properties: {
              user: userName || 'Unknown',
              version: version || 'Unknown',
              sentBy: sentBy || 'Unknown',
              initialCountdownSeconds: initialCountdown.toString(),
              timestamp: new Date().toISOString()
            }
          });
          
          onReload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, isPaused, countdown, onReload]);

  const handleCancel = () => {
    // Track cancellation in Application Insights
    appInsights.trackEvent({
      name: 'VersionControl_UserCancelledReload',
      properties: {
        user: userName || 'Unknown',
        version: version || 'Unknown',
        sentBy: sentBy || 'Unknown',
        timeLeftSeconds: countdown.toString(),
        initialCountdownSeconds: initialCountdown.toString(),
        timestamp: new Date().toISOString()
      }
    });
    
    setIsPaused(true);
    onCancel();
  };

  const handleReloadNow = () => {
    // Track manual reload in Application Insights
    appInsights.trackEvent({
      name: 'VersionControl_UserClickedReload',
      properties: {
        user: userName || 'Unknown',
        version: version || 'Unknown',
        sentBy: sentBy || 'Unknown',
        isMandatory: 'true',
        timeLeftSeconds: countdown.toString(),
        initialCountdownSeconds: initialCountdown.toString(),
        timestamp: new Date().toISOString()
      }
    });
    
    onReload();
  };

  // Calculate progress percentage
  const progress = ((initialCountdown - countdown) / initialCountdown) * 100;

  // Determine urgency color based on remaining time
  const getUrgencyColor = () => {
    const percentage = (countdown / initialCountdown) * 100;
    if (percentage > 50) return 'info';
    if (percentage > 25) return 'warning';
    return 'error';
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="mandatory-reload-dialog-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: 4,
          borderColor: getUrgencyColor() + '.main'
        }
      }}
    >
      <DialogTitle 
        id="mandatory-reload-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          pb: 1
        }}
      >
        <WarningOutlined style={{ fontSize: '1.5rem', color: '#ff9800' }} />
        Mandatory Update Required
      </DialogTitle>

      <DialogContent>
        <Alert severity={getUrgencyColor()} sx={{ mb: 2 }}>
          {message}
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          A new version of the application is available and must be loaded.
          {sentBy && (
            <>
              <br />
              <Typography component="span" variant="caption" color="text.secondary">
                Requested by: {sentBy}
              </Typography>
            </>
          )}
        </DialogContentText>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              New version: <strong>{version}</strong>
            </Typography>
            <Typography 
              variant="body2" 
              color={getUrgencyColor() + '.main'}
              fontWeight="bold"
            >
              {countdown}s
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            color={getUrgencyColor()}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <DialogContentText variant="caption" color="text.secondary">
          The application will reload automatically in {countdown} seconds.
          {!isPaused && ' You can reload now or cancel to postpone.'}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleCancel}
          startIcon={<CloseOutlined />}
          disabled={countdown === 0}
        >
          Cancel
        </Button>
        <Button
          onClick={handleReloadNow}
          variant="contained"
          color={getUrgencyColor()}
          startIcon={<ReloadOutlined />}
          autoFocus
        >
          Reload Now
        </Button>
      </DialogActions>
    </Dialog>
  );
}

